import { useState, useCallback } from "react";
import { apiFetch } from "../../../../api";
import s from "./FeedbackCard.module.css";

const FEELINGS = [
  { value: 1, label: "Boring" },
  { value: 2, label: "Good" },
  { value: 3, label: "Exciting" },
];

function FaceSvg({ type, size = 36 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <circle cx="16" cy="16" r="14" fill="currentColor" fillOpacity="0.08" stroke="currentColor" strokeWidth="1.5" />
      {type === "boring" && (
        <>
          <line x1="10" y1="13" x2="13" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <line x1="19" y1="13" x2="22" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <line x1="11" y1="20" x2="21" y2="20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </>
      )}
      {type === "good" && (
        <>
          <circle cx="11.5" cy="13" r="1.3" fill="currentColor" />
          <circle cx="20.5" cy="13" r="1.3" fill="currentColor" />
          <path d="M11 18.5 Q16 23 21 18.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
        </>
      )}
      {type === "excited" && (
        <>
          <path d="M10 13.5 Q11.5 11.5 13 13.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
          <path d="M19 13.5 Q20.5 11.5 22 13.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
          <path d="M10 18 Q16 25.5 22 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
        </>
      )}
    </svg>
  );
}

const FACE_TYPE = { 1: "boring", 2: "good", 3: "excited" };

export default function FeedbackCard({ storyId, moduleId, onDone }) {
  const [feeling, setFeeling] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!feeling) return;
    setIsSubmitting(true);
    try {
      await apiFetch(`/api/v1/dashboard/modules/${storyId}/${moduleId}/feedback`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feeling, feedback: feedbackText.trim() || null }),
      });
    } catch { /* feedback is optional */ }
    finally {
      setIsSubmitting(false);
      onDone();
    }
  }, [storyId, moduleId, feeling, feedbackText, onDone]);

  return (
    <div className={s.feedbackCard}>
      <div className={s.feedbackDivider}>
        <div className={s.feedbackDividerLine} />
        <span className={s.feedbackDividerDot} />
        <div className={s.feedbackDividerLine} />
      </div>

      <span className={s.feedbackLabel}>How did this feel?</span>

      <div className={s.emojiRow}>
        {FEELINGS.map((f, i) => (
          <button
            key={f.value}
            className={`${s.emojiBtn} ${feeling === f.value ? s.emojiSelected : ""}`}
            onClick={() => setFeeling(f.value)}
            type="button"
            style={{ animationDelay: `${0.25 + i * 0.08}s` }}
          >
            <span className={s.emojiGlyph}><FaceSvg type={FACE_TYPE[f.value]} /></span>
            <span className={s.emojiText}>{f.label}</span>
          </button>
        ))}
      </div>

      <div className={s.feedbackTextareaWrap}>
        <textarea
          className={s.feedbackTextarea}
          placeholder="Tell us more... (optional)"
          value={feedbackText}
          onChange={(e) => setFeedbackText(e.target.value)}
          rows={3}
        />
      </div>

      <div className={s.feedbackActions}>
        <button
          type="button"
          className={s.feedbackSubmitBtn}
          onClick={handleSubmit}
          disabled={!feeling || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <div className={s.feedbackBtnSpinner} />
              <span>Sending...</span>
            </>
          ) : (
            <>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z" />
              </svg>
              <span>Share Feedback</span>
            </>
          )}
        </button>
        <button className={s.feedbackSkipBtn} onClick={onDone} type="button">
          Skip for now
        </button>
      </div>
    </div>
  );
}
